import classNames from 'classnames';
import { ForwardedRef, ReactElement } from 'react';

import List, { ListItem, ListProps } from './List';

import styles from '../../styles/lists/grid-list.module.css';

export interface GridListProps<T = ListItem> extends ListProps<T> {
    count?: number | null;
    columns?: number | null;
    minColumns?: number | null;
    maxColumns?: number | 'auto' | null;
    multiplier?: number | null;
    autoColumns?: boolean;
    cardWidth?: number | null;
}

function GridList<T = ListItem>({
    items = null,
    count = null,
    columns = null,
    minColumns = null,
    maxColumns = null,
    multiplier = null,
    autoColumns = false,
    cardWidth = null,
    className = null,
    itemsClassName = null,
    itemsStyle = null,
    itemClassName = null,
    cardClassName = null,
    ref: listRef = null,
    ...props
}: GridListProps<T> & { ref?: ForwardedRef<HTMLDivElement> | null }) {
    const itemsCount = items !== null ? items.length : count;
    const finalCount =
        multiplier || (count !== null && itemsCount > count * 0.5 ? count : itemsCount);
    let columnsTemplate = null;
    if (columns !== null) {
        columnsTemplate = `repeat(${columns}, 1fr)`;
    } else if (autoColumns) {
        columnsTemplate = `repeat(auto-fit, minmax(${cardWidth}px, 1fr))`;
    }
    return (
        <List<T>
            {...props}
            ref={listRef}
            items={items}
            cardWidth={cardWidth}
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
            itemsClassName={classNames([
                styles.items,
                {
                    [styles.min2columns]: minColumns === 2,
                    [styles.min3columns]: minColumns === 3,
                    [styles.max3columns]:
                        maxColumns === 3 || (maxColumns === 'auto' && itemsCount === 3),
                    [styles.max4columns]:
                        maxColumns === 4 || (maxColumns === 'auto' && itemsCount === 4),
                    [styles.max5columns]:
                        maxColumns === 5 || (maxColumns === 'auto' && itemsCount === 5),
                    [styles.max6columns]:
                        maxColumns === 6 || (maxColumns === 'auto' && itemsCount === 6),
                    [styles.count4]: finalCount % 4 === 0,
                    [styles.count6]: finalCount % 6 === 0,
                    [styles.count8]: finalCount % 8 === 0,
                    [styles.count12]: finalCount % 12 === 0,
                },
                itemsClassName,
            ])}
            itemsStyle={
                columnsTemplate !== null
                    ? {
                          ...itemsStyle,
                          gridTemplateColumns: columnsTemplate,
                      }
                    : itemsStyle
            }
            itemClassName={classNames([
                styles.item,
                {
                    [itemClassName]: itemClassName !== null,
                },
            ])}
            cardClassName={classNames([
                styles.card,
                {
                    [cardClassName]: cardClassName !== null,
                },
            ])}
        />
    );
}

export default GridList as <T = ListItem>(
    p: GridListProps<T> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReactElement;
